import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check, ChevronDown, Loader2, Lock, Undo2 } from 'lucide-react';
import { CANONICAL_BUCKETS } from '@/lib/genres';
import api from '@/lib/api';
import { toast } from 'sonner';

/*
 * RecheckSuggestionsModal
 *
 * Workflow:
 *   - Caller passes in a list of `suggestions` (computed by
 *     POST /api/cards/reclassify-suggestions).
 *   - User can per-row:
 *       1. Tap "Move ▾" → pick "Move to <suggested>" (auto) OR
 *          "Choose different crate…" → opens canonical-buckets dropdown
 *          and the move applies on selection.
 *       2. Tap "Keep where it is" → calls /lock-genre to mark the card
 *          with genre_overridden_by_user so future re-checks skip it.
 *   - Or use the master "Move all to suggested" button at the bottom.
 *   - After any move (single or batch), an Undo banner appears at the
 *     bottom of the modal for 10 seconds with the inverse action.
 *
 * Closure: when the suggestion list reaches empty (everything actioned),
 * the modal auto-closes after a small celebration toast.
 */

const UNDO_WINDOW_MS = 10000;

export default function RecheckSuggestionsModal({
  open,
  onOpenChange,
  scope, // 'all' | 'crate'
  scopeLabel, // e.g. "House" when scope === 'crate'
  suggestions: initialSuggestions,
  onAfterAction, // callback to refresh the parent list when modal closes
}) {
  // Local list that shrinks as the user acts. We keep the original
  // suggestion (with current_bucket / suggested_bucket) so the undo can
  // restore the previous state.
  const [items, setItems] = useState(initialSuggestions || []);
  const [busyId, setBusyId] = useState(null);
  const [moveAllBusy, setMoveAllBusy] = useState(false);
  // Stack of recent actions for Undo. Each entry:
  //   { type: 'move'|'lock'|'move-all', actions: [{cardId, fromBucket, wasOverridden}] }
  const [undoEntry, setUndoEntry] = useState(null);
  const [undoTimeLeft, setUndoTimeLeft] = useState(0);

  // Reset internal state when caller opens with a fresh suggestion list.
  useEffect(() => {
    setItems(initialSuggestions || []);
    setUndoEntry(null);
    setUndoTimeLeft(0);
    setBusyId(null);
    setMoveAllBusy(false);
  }, [initialSuggestions, open]);

  // Tick down the undo countdown so the user sees how long they have left.
  useEffect(() => {
    if (!undoEntry) return undefined;
    setUndoTimeLeft(UNDO_WINDOW_MS);
    const start = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      const left = Math.max(0, UNDO_WINDOW_MS - elapsed);
      setUndoTimeLeft(left);
      if (left <= 0) {
        clearInterval(id);
        setUndoEntry(null);
      }
    }, 250);
    return () => clearInterval(id);
  }, [undoEntry]);

  const headline = useMemo(() => {
    const n = items.length;
    if (n === 0) return 'All sorted — nothing left to review.';
    if (scope === 'all') {
      return `${n} ${n === 1 ? 'track' : 'tracks'} across your collection look like ${n === 1 ? 'it' : 'they'} should move`;
    }
    return `${n} ${n === 1 ? 'track' : 'tracks'} in ${scopeLabel || 'this crate'} look like ${n === 1 ? 'it' : 'they'} belong elsewhere`;
  }, [items.length, scope, scopeLabel]);

  // Optimistically remove a row from the list (avoids the user seeing
  // stale rows mid-flight). Returns the row we removed so callers can
  // include it in an Undo action.
  const removeRow = (cardId) => {
    let removed = null;
    setItems((prev) => {
      const next = prev.filter((x) => {
        if (x.card_id === cardId) { removed = x; return false; }
        return true;
      });
      return next;
    });
    return removed;
  };

  const doMove = async (suggestion, targetBucket) => {
    setBusyId(suggestion.card_id);
    try {
      await api.patch(`/cards/${suggestion.card_id}/genre`, {
        genre_bucket: targetBucket,
      });
      removeRow(suggestion.card_id);
      setUndoEntry({
        type: 'move',
        actions: [{
          cardId: suggestion.card_id,
          fromBucket: suggestion.current_bucket,
          toBucket: targetBucket,
          suggestion,
        }],
      });
      toast.success(`Moved to ${targetBucket}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Move failed');
    } finally {
      setBusyId(null);
    }
  };

  const doKeep = async (suggestion) => {
    setBusyId(suggestion.card_id);
    try {
      await api.post(`/cards/${suggestion.card_id}/lock-genre`);
      removeRow(suggestion.card_id);
      // No undo for "Keep" — re-doing it would just re-suggest a move
      // that the user already declined; the lock is the whole point.
      setUndoEntry(null);
      toast.success(`Kept in ${suggestion.current_bucket}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not lock');
    } finally {
      setBusyId(null);
    }
  };

  const doMoveAll = async () => {
    if (items.length === 0) return;
    setMoveAllBusy(true);
    const succeeded = [];
    try {
      for (const s of items) {
        try {
          await api.patch(`/cards/${s.card_id}/genre`, {
            genre_bucket: s.suggested_bucket,
          });
          succeeded.push({
            cardId: s.card_id,
            fromBucket: s.current_bucket,
            toBucket: s.suggested_bucket,
            suggestion: s,
          });
        } catch (e) {
          // Continue with the rest even if one fails
        }
      }
      setItems([]);
      setUndoEntry({ type: 'move-all', actions: succeeded });
      toast.success(
        `Moved ${succeeded.length} ${succeeded.length === 1 ? 'track' : 'tracks'}`,
      );
    } finally {
      setMoveAllBusy(false);
    }
  };

  const doUndo = async () => {
    if (!undoEntry || undoEntry.actions.length === 0) return;
    const actions = undoEntry.actions;
    setUndoEntry(null);
    setUndoTimeLeft(0);
    let restored = 0;
    for (const a of actions) {
      try {
        await api.patch(`/cards/${a.cardId}/genre`, {
          genre_bucket: a.fromBucket,
        });
        // Re-insert into the modal so the user can re-decide.
        setItems((prev) => [a.suggestion, ...prev]);
        restored += 1;
      } catch (e) {
        // best-effort revert
      }
    }
    toast.success(`Undone — restored ${restored} ${restored === 1 ? 'track' : 'tracks'}`);
  };

  const handleClose = useCallback(() => {
    // Tell the parent something likely changed so it can refresh.
    if (onAfterAction) onAfterAction();
    onOpenChange(false);
  }, [onAfterAction, onOpenChange]);

  // Auto-close when the user has actioned everything and the undo
  // window has expired. (We don't auto-close while undo is still
  // available because the user might want to revert.)
  useEffect(() => {
    if (open && items.length === 0 && !undoEntry && !moveAllBusy) {
      const id = setTimeout(() => handleClose(), 800);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [items.length, undoEntry, open, moveAllBusy, handleClose]);

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : handleClose())}>
      <DialogContent
        className="max-w-2xl w-[95vw] rounded-2xl bg-card p-0 overflow-hidden"
        data-testid="recheck-suggestions-modal"
      >
        <DialogHeader className="p-5 pb-3">
          <DialogTitle className="font-display text-xl">
            Re-check suggestions
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {headline}
          </DialogDescription>
        </DialogHeader>

        {/* Body — scrollable list of suggestion rows */}
        <div
          className="max-h-[60vh] overflow-y-auto px-5 pb-2"
          data-testid="recheck-suggestions-list"
        >
          {items.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <Check className="w-6 h-6 mx-auto mb-2 text-[hsl(var(--primary))]" />
              All sorted! This modal will close in a moment.
            </div>
          ) : (
            <ul className="space-y-2">
              {items.map((s) => (
                <li
                  key={s.card_id}
                  className="flex items-center gap-3 p-2.5 rounded-xl border border-border bg-background/40"
                  data-testid={`recheck-row-${s.card_id}`}
                >
                  <div className="w-11 h-11 shrink-0 rounded-md overflow-hidden bg-secondary">
                    {s.album_art_url ? (
                      <img
                        src={s.album_art_url}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate font-medium">{s.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{s.artist}</div>
                    <div className="text-[11px] mt-0.5 flex items-center gap-1 text-muted-foreground">
                      <span className="px-1.5 py-0.5 rounded bg-secondary/60">
                        {s.current_bucket || 'Other'}
                      </span>
                      <ArrowRight className="w-3 h-3 opacity-60" />
                      <span className="px-1.5 py-0.5 rounded bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] font-medium">
                        {s.suggested_bucket}
                      </span>
                    </div>
                  </div>

                  {/* Per-row controls */}
                  <div className="shrink-0 flex items-center gap-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          disabled={busyId === s.card_id || moveAllBusy}
                          className="rounded-lg h-8 text-xs px-2.5 gap-1"
                          data-testid={`recheck-row-move-${s.card_id}`}
                        >
                          {busyId === s.card_id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <>
                              Move
                              <ChevronDown className="w-3 h-3" />
                            </>
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          Suggested
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                          onSelect={(e) => { e.preventDefault(); doMove(s, s.suggested_bucket); }}
                          className="cursor-pointer text-sm"
                          data-testid={`recheck-row-move-suggested-${s.card_id}`}
                        >
                          <Check className="w-3.5 h-3.5 mr-2 text-[hsl(var(--primary))]" />
                          {s.suggested_bucket}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          Choose another crate
                        </DropdownMenuLabel>
                        {CANONICAL_BUCKETS
                          .filter((b) => b !== s.suggested_bucket && b !== s.current_bucket)
                          .map((b) => (
                            <DropdownMenuItem
                              key={b}
                              onSelect={(e) => { e.preventDefault(); doMove(s, b); }}
                              className="cursor-pointer text-xs"
                              data-testid={`recheck-row-move-${b.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${s.card_id}`}
                            >
                              {b}
                            </DropdownMenuItem>
                          ))}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busyId === s.card_id || moveAllBusy}
                      onClick={() => doKeep(s)}
                      className="rounded-lg h-8 text-xs px-2 text-muted-foreground hover:text-foreground"
                      title="Keep where it is — future re-checks will skip this track"
                      data-testid={`recheck-row-keep-${s.card_id}`}
                    >
                      <Lock className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer — master Move-all button + undo bar */}
        <DialogFooter className="p-5 pt-3 border-t border-border bg-background/40 flex-col items-stretch gap-2 sm:flex-col sm:items-stretch sm:gap-2">
          {items.length > 0 && (
            <Button
              onClick={doMoveAll}
              disabled={moveAllBusy || busyId !== null}
              className="rounded-xl w-full"
              data-testid="recheck-move-all-button"
            >
              {moveAllBusy ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Moving…</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Move all {items.length} to suggested {items.length === 1 ? 'crate' : 'crates'}</>
              )}
            </Button>
          )}

          {undoEntry && (
            <div
              className="flex items-center justify-between gap-3 rounded-lg border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 px-3 py-2 text-sm"
              data-testid="recheck-undo-bar"
              role="status"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium leading-tight">
                  {undoEntry.type === 'move-all' ? `Moved ${undoEntry.actions.length} ${undoEntry.actions.length === 1 ? 'track' : 'tracks'}` : `Moved 1 track`}
                </p>
                <p className="text-[11px] text-muted-foreground leading-tight">
                  Undo available for {Math.ceil(undoTimeLeft / 1000)}s
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={doUndo}
                className="rounded-lg h-8 text-xs shrink-0"
                data-testid="recheck-undo-button"
              >
                <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Undo
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
